import { useState } from "react";
import api from "../api/axios";
import Layout from "../components/Layout";
import Card from "../components/UI/Card";
import Button from "../components/UI/Button";

async function downloadTemplate() {
  try {
    const { data } = await api.get("/records/template", { responseType: "blob" });
    const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "upload_template.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    alert("Failed to download template");
  }
}

export default function UploadCsv() {
  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  async function upload() {
    if (!file) return setMsg("Please select a CSV file");
    setLoading(true);
    setMsg("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/records/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setMsg(`✅ Upload success: inserted ${data.inserted}/${data.totalRows}`);
    } catch (err) {
      setMsg(err?.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout>
      <div className="flex flex-col gap-5">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest opacity-40 mb-1">Data</div>
          <h1 className="text-2xl font-bold">Upload CSV</h1>
          <p className="text-sm opacity-40 mt-1">Export your Excel sheet as CSV and upload here.</p>
        </div>

        <div className="flex gap-5 items-start">
          {/* Template download */}
          <Card className="p-5 flex flex-col gap-3 flex-1">
            <div>
              <div className="text-sm font-semibold">CSV Template</div>
              <div className="text-xs opacity-40 mt-0.5">Download the Excel template and fill with your data in the correct format.</div>
            </div>
            <button
              onClick={downloadTemplate}
              className="w-full px-4 py-2 rounded-xl border border-[rgb(var(--border))] text-sm font-semibold hover:border-[rgb(var(--primary))] hover:text-[rgb(var(--primary))] transition-colors"
              style={{ color: "rgb(var(--fg))", background: "transparent" }}
            >
              Download Template
            </button>
          </Card>

          {/* Upload area */}
          <Card className="p-5 flex-1">
            <div className="text-sm font-semibold opacity-60 mb-3">Select CSV File</div>

            <label
              className="flex flex-col items-center justify-center gap-3 border-2 border-dashed rounded-2xl p-8 cursor-pointer transition-colors hover:border-[rgb(var(--primary))] hover:bg-[rgba(var(--primary),0.03)]"
              style={{ borderColor: file ? "rgb(var(--primary))" : "rgb(var(--border))" }}
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center text-xl"
                style={{ background: "rgba(var(--primary),0.12)", color: "rgb(var(--primary))" }}
              >
                {file ? "✓" : "↑"}
              </div>
              <div className="text-center">
                <div className="text-sm font-semibold">
                  {file ? file.name : "Click to browse"}
                </div>
                {!file && <div className="text-xs opacity-40 mt-1">Supports .csv and .xlsx files</div>}
                {file && <div className="text-xs opacity-40 mt-1">{(file.size / 1024).toFixed(1)} KB</div>}
              </div>
              <input type="file" accept=".csv,.xlsx" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>

            {msg && (
              <div className={`mt-4 px-3 py-2.5 rounded-xl text-sm border ${
                msg.startsWith("✅")
                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                  : "bg-red-50 border-red-200 text-red-600"
              }`}>
                {msg}
              </div>
            )}

            <Button loading={loading} disabled={!file} onClick={upload} className="mt-4 w-full">
              {loading ? "Uploading..." : "Upload to Database"}
            </Button>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
